import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpIcon } from '@heroicons/react/24/solid';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { HeroBackground } from '@/components/HeroBackground';
import { GlobeView } from '@/components/Globe';
import { StripeWave } from '@/components/StripeWave';
import PublicHeader from '@/components/ui/public-header';
import Footer from '@/components/ui/footer';
import Logo from '@/components/ui/logo';
import LogoIcon from '@/components/ui/logo-icon';
import { useAppConfig } from '@/lib/hooks/useAppConfig';
import { WaitlistForm } from '@/components/WaitlistForm';

const TYPED_PROMPTS = [
  'Create Sales Agent',
  'Create Reply Agent',
  'Find Leads',
];

function useScrollReveal<T extends HTMLElement>(
  apply: (el: T, progress: number) => void,
  startVH = 1.0,
  endVH = 0.20,
) {
  const ref = useRef<T | null>(null);
  const applyRef = useRef(apply);
  applyRef.current = apply;
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let raf = 0;
    let inView = false;
    const update = () => {
      raf = 0;
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight;
      const start = vh * startVH;
      const end = vh * endVH;
      const p = Math.min(1, Math.max(0, (start - rect.top) / (start - end)));
      applyRef.current(el, p);
    };
    const onScroll = () => {
      if (!inView || raf) return;
      raf = requestAnimationFrame(update);
    };
    const io = new IntersectionObserver(
      (entries) => {
        inView = entries[0]?.isIntersecting ?? false;
        if (inView) update();
      },
      { rootMargin: '50% 0px' },
    );
    io.observe(el);
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      io.disconnect();
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [startVH, endVH]);
  return ref;
}

// Discrete per-letter palette (same hue family as the StripeWave canvas).
const WAVE_PALETTE = ['#3b82f6', '#8b5cf6', '#ec4899', '#f59e0b', '#14b8a6'];


function RevealText({
  text,
  blur = 0,
  rise = 18,
  startVH = 1.0,
  endVH = 0.20,
  charSpread = 0.25,
  rainbowWord,
  /** When true, animate over `duration` ms when the element enters view. */
  playOnce = false,
  duration = 1200,
  /** Drive progress from this target's scroll-through-viewport instead of the
   *  element itself. Use inside sticky/pinned containers — pass the pinned
   *  section so the cascade fires as the user scrolls through the section. */
  scrollTarget,
  scrollStart = 0,
  scrollEnd = 0.15,
}: {
  text: string;
  blur?: number;
  rise?: number;
  startVH?: number;
  endVH?: number;
  charSpread?: number;
  rainbowWord?: string;
  playOnce?: boolean;
  duration?: number;
  scrollTarget?: () => HTMLElement | null;
  scrollStart?: number;
  scrollEnd?: number;
}) {
  const ref = useRef<HTMLSpanElement | null>(null);
  const charRefs = useRef<Array<HTMLSpanElement | null>>([]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const chars = charRefs.current;
    const N = chars.length;
    let raf = 0;
    let inView = false;

    const apply = (p: number) => {
      const blockEased = 1 - Math.pow(1 - p, 3);
      el.style.transform = `translateY(${(1 - blockEased) * rise}px)`;
      for (let i = 0; i < N; i++) {
        const c = chars[i];
        if (!c) continue;
        const charStart = (i / N) * (1 - charSpread);
        const charEnd = charStart + charSpread;
        const local = Math.min(1, Math.max(0, (p - charStart) / (charEnd - charStart || 1)));
        const eased = 1 - Math.pow(1 - local, 3);
        c.style.opacity = String(eased);
        c.style.filter = `blur(${(1 - eased) * blur}px)`;
      }
    };

    if (scrollTarget) {
      const update = () => {
        raf = 0;
        const t = scrollTarget();
        if (!t) return;
        const rect = t.getBoundingClientRect();
        const total = Math.max(1, rect.height - window.innerHeight);
        const raw = Math.min(1, Math.max(0, -rect.top / total));
        // Remap raw section progress into [scrollStart, scrollEnd] = [0, 1]
        // so the cascade plays inside that scroll window and reverses on the way back.
        const p = Math.min(1, Math.max(0, (raw - scrollStart) / Math.max(0.0001, scrollEnd - scrollStart)));
        apply(p);
      };
      const onScroll = () => { if (raf) return; raf = requestAnimationFrame(update); };
      update();
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll);
      return () => {
        if (raf) cancelAnimationFrame(raf);
        window.removeEventListener('scroll', onScroll);
        window.removeEventListener('resize', onScroll);
      };
    }

    if (playOnce) {
      let startT = 0;
      const tick = (t: number) => {
        if (!startT) startT = t;
        const p = Math.min(1, (t - startT) / duration);
        apply(p);
        if (p < 1) raf = requestAnimationFrame(tick);
      };
      apply(0);
      const io = new IntersectionObserver(
        (entries) => {
          if (entries[0]?.isIntersecting) {
            raf = requestAnimationFrame(tick);
            io.disconnect();
          }
        },
        { threshold: 0.15 },
      );
      io.observe(el);
      return () => {
        io.disconnect();
        if (raf) cancelAnimationFrame(raf);
      };
    }

    const update = () => {
      raf = 0;
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight;
      const start = vh * startVH;
      const end = vh * endVH;
      const p = Math.min(1, Math.max(0, (start - rect.top) / (start - end)));
      apply(p);
    };
    const onScroll = () => {
      if (!inView || raf) return;
      raf = requestAnimationFrame(update);
    };
    const io = new IntersectionObserver(
      (entries) => {
        inView = entries[0]?.isIntersecting ?? false;
        if (inView) update();
      },
      { rootMargin: '50% 0px' },
    );
    io.observe(el);
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      io.disconnect();
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [blur, rise, startVH, endVH, charSpread, text, playOnce, duration, scrollTarget, scrollStart, scrollEnd]);

  charRefs.current = [];
  const words = text.split(' ');
  const isRainbow = (word: string) =>
    !!rainbowWord && word.replace(/[.,!?;:]+$/, '').toLowerCase() === rainbowWord.toLowerCase();
  return (
    <span ref={ref} aria-label={text}>
      {words.map((word, wi) => {
        const rainbow = isRainbow(word);
        return (
        <span
          key={wi}
          style={{ display: 'inline-block', whiteSpace: 'nowrap' }}
        >
          {Array.from(word).map((ch, ci) => (
            <span
              key={ci}
              ref={(el) => { charRefs.current.push(el); }}
              aria-hidden
              style={{
                display: 'inline-block',
                opacity: 0,
                filter: `blur(${blur}px)`,
                willChange: 'opacity, filter',
                ...(rainbow ? { color: WAVE_PALETTE[ci % WAVE_PALETTE.length] } : null),
              }}
            >
              {ch}
            </span>
          ))}
          {wi < words.length - 1 ? ' ' : ''}
        </span>
        );
      })}
    </span>
  );
}

function useTypingPlaceholder(prompts: string[]) {
  const [text, setText] = useState('');
  useEffect(() => {
    let idx = 0;
    let char = 0;
    let deleting = false;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;

    const tick = () => {
      if (cancelled) return;
      const full = prompts[idx];
      if (!deleting) {
        char += 1;
        setText(full.slice(0, char));
        if (char === full.length) {
          deleting = true;
          timer = setTimeout(tick, 1800);
          return;
        }
        timer = setTimeout(tick, 45 + Math.random() * 40);
      } else {
        char -= 1;
        setText(full.slice(0, char));
        if (char === 0) {
          deleting = false;
          idx = (idx + 1) % prompts.length;
          timer = setTimeout(tick, 300);
          return;
        }
        timer = setTimeout(tick, 22);
      }
    };
    timer = setTimeout(tick, 500);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [prompts]);
  return text;
}

function TypingTextarea() {
  const typedPart = useTypingPlaceholder(TYPED_PROMPTS);
  const placeholder = `Ask Falcon to ${typedPart}` || ' ';
  return (
    <Textarea
      placeholder={placeholder}
      className="min-h-[180px] resize-none bg-card shadow-2xl !rounded-card !px-8 !py-7 pr-24 !text-lg overflow-y-auto text-left"
    />
  );
}

export default function HomePage() {
  const { waitingListMode, isLoading: configLoading } = useAppConfig();

  // Drive the globe wrapper's vertical offset from the scroll position through
  // the pinned globe section: starts ~180px below centre, settles to centre.
  const globeWrapRef = useRef<HTMLDivElement | null>(null);
  const globeTextRef = useRef<HTMLDivElement | null>(null);
  const scrollCueRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = scrollCueRef.current;
    if (!el) return;
    let raf = 0;
    const update = () => {
      raf = 0;
      // Full opacity at top of page, fades to 0 over the first 250px of scroll.
      const p = Math.min(1, Math.max(0, window.scrollY / 250));
      el.style.opacity = String(1 - p);
    };
    const onScroll = () => { if (raf) return; raf = requestAnimationFrame(update); };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener('scroll', onScroll);
    };
  }, []);
  useEffect(() => {
    const target = document.querySelector('[data-globe-scroll-target]') as HTMLElement | null;
    const wrap = globeWrapRef.current;
    if (!target || !wrap) return;
    let raf = 0;
    const update = () => {
      raf = 0;
      const rect = target.getBoundingClientRect();
      const total = Math.max(1, rect.height - window.innerHeight);
      const p = Math.min(1, Math.max(0, -rect.top / total));
      const eased = 1 - Math.pow(1 - p, 3);
      // Mirror the CSS --globe-scale formula. Sphere visible diameter at full
      // camera zoom is ~964px at scale 1 (see globals.css comment).
      const widthRatio = window.innerWidth / 1440;
      const heightRatio = (window.innerHeight - 60) / 1000;
      const globeScale = Math.max(0.45, Math.min(1.333, Math.min(widthRatio, heightRatio)));
      const radiusPx = 482 * globeScale;
      const maxOffset = Math.max(0, window.innerHeight / 2 - radiusPx - 30);
      const offset = (1 - eased) * Math.min(140, maxOffset);
      wrap.style.transform = `translate(-50%, calc(-50% + ${offset}px)) scale(var(--globe-scale, 1))`;
      // Fade the globe in over the first quarter of the pinned scroll.
      const FADE_END = 0.25;
      wrap.style.opacity = String(Math.min(1, p / FADE_END));
    };
    const onScroll = () => { if (raf) return; raf = requestAnimationFrame(update); };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, []);

  const chatBoxRef = useScrollReveal<HTMLDivElement>((el, p) => {
    // easeOutCubic — fast in, gentle settle at the end
    const eased = 1 - Math.pow(1 - p, 3);
    el.style.opacity = String(eased);
    el.style.transform = `translateY(${(1 - eased) * 40}px)`;
  });

  return (
    <section className="flex flex-col min-h-screen overflow-visible bg-surface-marketing">
      {configLoading ? <div className="h-12 bg-surface-marketing" /> : waitingListMode ? null : <PublicHeader />}
      {/* In waitlist mode the hero is the only content, so let main
          take the full viewport and centre the hero vertically inside. */}
      <main className={waitingListMode ? 'flex-1 flex items-center w-full' : ''}>
        {/* Hero Section */}
        <section className={`bg-surface-marketing ${waitingListMode ? 'py-4 sm:py-6 lg:py-8 px-8 sm:px-16 lg:px-32 w-full' : 'px-3 sm:px-8 lg:px-20 pb-6 -mt-4'}`}>
          <div className={`rounded-panel overflow-hidden relative ${waitingListMode ? 'h-[88vh]' : 'h-[78vh]'}`}>
            <HeroBackground className="absolute inset-0 w-full h-full" timeOffset={264} />
            <div className="absolute inset-0 bg-gradient-to-b from-marketing-hero-wash/30 to-surface-marketing/70 pointer-events-none" />
            <div className="relative h-full flex items-center justify-center px-4 sm:px-6 lg:px-8">
              <div className="max-w-7xl mx-auto">
                <div className="text-center">
                  {(configLoading || waitingListMode) && (
                    <div className="flex justify-center mb-6 min-h-[40px] [&_span]:!text-content-strong overflow-hidden">
                      {!configLoading && (
                        <div
                          className="flex items-center -ml-3"
                          style={{
                            transform: 'translateY(110%)',
                            animation: 'slideUpClipped 0.8s cubic-bezier(0.16, 1, 0.3, 1) 0s forwards',
                          }}
                        >
                          <LogoIcon size={40} color="black" className="ml-[calc(1rem-3px)]" />
                          <div className="-ml-2"><Logo size="lg" /></div>
                        </div>
                      )}
                    </div>
                  )}
                  <h1
                    className="text-[3.3rem] text-content-strong tracking-[-0.01em] sm:text-[4.4rem] md:text-[6.05rem] leading-[1.0]"
                    style={{ fontFamily: "'Founders Grotesk', sans-serif", fontWeight: 500 }}
                  >
                    <span style={{ display: 'block', overflow: 'hidden', paddingBottom: '0.05em' }}>
                      <span
                        style={{
                          display: 'block',
                          transform: 'translateY(110%)',
                          animation: 'slideUpClipped 0.9s cubic-bezier(0.16, 1, 0.3, 1) 0.1s forwards',
                        }}
                      >
                        The UK
                        <span style={{ fontFamily: "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif", marginLeft: '-0.05em', marginRight: '-0.05em' }}>'</span>
                        s Greatest
                      </span>
                    </span>
                    <span style={{ display: 'block', overflow: 'hidden', paddingBottom: '0.05em' }}>
                      <span
                        style={{
                          display: 'block',
                          transform: 'translateY(110%)',
                          animation: 'slideUpClipped 0.9s cubic-bezier(0.16, 1, 0.3, 1) 0.2s forwards',
                        }}
                      >
                        AI Outreach Agent.
                      </span>
                    </span>
                  </h1>
                  <p
                    className="mt-6 sm:mt-8 md:mt-10 text-xl text-content-strong max-w-3xl mx-auto font-normal"
                    style={{ overflow: 'hidden' }}
                  >
                    <span
                      style={{
                        display: 'inline-block',
                        transform: 'translateY(110%)',
                        animation: 'slideUpClipped 0.8s cubic-bezier(0.16, 1, 0.3, 1) 0.4s forwards',
                      }}
                    >
                      An AI agent that researches prospects in real-time, finds the perfect moment to reach out, and writes messages so personal they don't feel like outreach.
                    </span>
                  </p>
                  <div className="mt-10 sm:mt-12 md:mt-14 flex justify-center items-center overflow-hidden w-full">
                    <div
                      className={waitingListMode ? 'w-full max-w-md' : ''}
                      style={{
                        transform: 'translateY(110%)',
                        animation: 'slideUpClipped 0.8s cubic-bezier(0.16, 1, 0.3, 1) 0.6s forwards',
                      }}
                    >
                      {configLoading ? null : waitingListMode ? (
                        <WaitlistForm source="hero" />
                      ) : (
                        <Button asChild variant="primary" size="lg" className="px-8 py-8 text-md">
                          <Link to="/sign-up">
                            Get full access for 14 days
                          </Link>
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Everything below the hero is hidden in waiting-list mode —
            the hero alone (with the waitlist form) is the whole page
            until launch. Also gated on !configLoading so the post-hero
            content doesn't flash visible for a frame before the config
            fetch resolves and we know it's waitlist mode. */}
        {!configLoading && !waitingListMode && (
        <>
        {/* Scroll cue */}
        <div ref={scrollCueRef} className="bg-surface-marketing text-center pt-3" style={{ transition: 'opacity 0.15s linear' }}>
          <p className="text-xs uppercase tracking-[0.18em]">
            Scroll to find out more
          </p>
        </div>

        {/* Subtitle + chat box */}
        <section className="pt-24 pb-20 bg-surface-marketing relative z-10" style={{ contentVisibility: 'auto', containIntrinsicSize: 'auto 700px' }}>
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <p
              className="text-4xl sm:text-5xl md:text-6xl text-content-strong leading-tight"
              style={{ fontFamily: "'Aspekta Variable', 'Manrope', sans-serif", fontWeight: 900 }}
            >
              <RevealText text="Falcon can automate all of your outreach." startVH={0.8} endVH={0.35} />
            </p>

            <div ref={chatBoxRef} className="mt-8 max-w-3xl mx-auto" style={{ opacity: 0, transform: 'translateY(40px)' }}>
              <div className="relative">
                <TypingTextarea />
                <div className="absolute bottom-4 right-4 rounded-full p-[2px] bg-gradient-to-br from-surface-marketing to-marketing-glow">
                  <Button asChild variant="primary" size="lg" className="rounded-full h-11 w-20 p-0">
                    <Link to="/sign-up" aria-label="Get started">
                      <ArrowUpIcon className="!h-7 !w-7 !text-marketing-cta-glyph" />
                    </Link>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Workflows — placeholder */}
        <section className="py-32 bg-surface-marketing hidden">
          <div className="max-w-7xl mx-auto px-8 lg:px-20">
            <div className="text-right mb-12 flex flex-col items-end">
              <h2
                className="text-3xl sm:text-4xl md:text-5xl text-content-strong leading-tight mb-4"
                style={{ fontFamily: "'Aspekta Variable', 'Manrope', sans-serif", fontWeight: 700 }}
              >
                <RevealText text="Workflows" rise={32} />
              </h2>
              <p className="text-lg max-w-md">
                <RevealText text="Placeholder copy. The workflows section lives here." rise={24} />
              </p>
            </div>
            <div className="rounded-card bg-card border border-dashed border-line-dashed h-[420px] flex items-center justify-center text-content-subtle-neutral text-sm">
              Workflows placeholder
            </div>
          </div>
        </section>

        {/* Globe — pinned, scroll-driven */}
        <section className="relative bg-surface-marketing -mt-80" data-globe-scroll-target style={{ height: '350vh' }}>
          <div className="sticky top-0 h-screen relative">
            {/* White gradient sits ABOVE the globe to back the text but fades
                fully transparent well before the UK area so it isn't washed. */}
            <div
              className="pointer-events-none"
              style={{
                position: 'absolute',
                inset: 0,
                top: 0,
                height: '60%',
                background:
                  'linear-gradient(to bottom, var(--surface-marketing) 0%, var(--surface-marketing) 45%, color-mix(in oklab, var(--surface-marketing) 0%, transparent) 80%)',
                zIndex: 1,
              }}
            />
            {/* Globe sits low at start of scroll, eases up to centre */}
            <div
              ref={globeWrapRef}
              className="pointer-events-none"
              style={{
                position: 'absolute',
                left: '50%',
                top: '50%',
                transform: 'translate(-50%, calc(-50% + min(140px, max(0px, 50vh - 512px * var(--globe-scale, 1))))) scale(var(--globe-scale, 1))',
                zIndex: 0,
              }}
            >
              <GlobeView size={500} paddingX={300} paddingY={300} />
            </div>
            {/* Text floats high over the globe — per-letter cascade handled by RevealText */}
            <div
              ref={globeTextRef}
              className="text-center"
              style={{
                position: 'absolute',
                left: '50%',
                top: '50%',
                transform: 'translate(-50%, calc(-50% - 180px * var(--public-scale, 1)))',
                zIndex: 2,
                width: '100%',
                maxWidth: '64rem',
                padding: '0 5rem',
                textShadow: 'none',
              }}
            >
              <h2
                className="text-3xl sm:text-4xl md:text-5xl text-content-strong leading-tight mb-4"
                style={{ fontFamily: "'Aspekta Variable', 'Manrope', sans-serif", fontWeight: 950 }}
              >
                <RevealText
                  text="Every UK data point. In one place."
                  rise={48}
                  scrollTarget={() => document.querySelector('[data-globe-scroll-target]') as HTMLElement | null}
                  scrollStart={0}
                  scrollEnd={0.18}
                />
              </h2>
              <p className="text-lg max-w-xl mx-auto" style={{ fontWeight: 500 }}>
                <RevealText
                  text="See your whole UK market, the buyers you actually want, and the distance between them. Then close it."
                  rise={32}
                  scrollTarget={() => document.querySelector('[data-globe-scroll-target]') as HTMLElement | null}
                  scrollStart={0.05}
                  scrollEnd={0.25}
                />
              </p>
            </div>
          </div>
        </section>

        {/* Stripe wave */}
        <section className="py-40 bg-surface-marketing hidden" style={{ contentVisibility: 'auto', containIntrinsicSize: 'auto 800px' }}>
          <div className="max-w-7xl mx-auto px-8 lg:px-20">
            <div className="text-left mb-0">
              <h2
                className="text-3xl sm:text-4xl md:text-5xl text-content-strong leading-tight mb-4"
                style={{ fontFamily: "'Aspekta Variable', 'Manrope', sans-serif", fontWeight: 700 }}
              >
                <RevealText text="Scale your outreach with ease." rise={32} />
              </h2>
              <p className="text-lg sm:text-xl max-w-xl">
                <RevealText text="And every email still feels natural." rise={24} />
              </p>
            </div>
            <div className="rounded-[3rem] overflow-hidden bg-surface-marketing -mt-40">
              <StripeWave height={460} background={[1, 1, 1]} />
            </div>
          </div>
        </section>

        {/* Bottom CTA */}
        <section className="pt-56 pb-16 bg-surface-marketing" style={{ contentVisibility: 'auto', containIntrinsicSize: 'auto 600px' }}>
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2
              className="text-3xl sm:text-4xl md:text-5xl text-content-strong leading-tight"
              style={{ fontFamily: "'Aspekta Variable', 'Manrope', sans-serif", fontWeight: 700 }}
            >
              Automate your outreach today.
            </h2>
            <div className="mt-10 flex justify-center">
              <Button asChild variant="primary" size="lg" className="px-8 py-8 text-md">
                <Link to="/sign-up">Get started for free</Link>
              </Button>
            </div>
          </div>
        </section>

        {/* Big image above the footer */}
        <section className="bg-surface-marketing hidden">
          <img
            src="/Group%20313.png"
            alt=""
            aria-hidden="true"
            className="block mx-auto w-full max-w-[95rem] h-auto -mb-[13rem]"
          />
        </section>
        </>
        )}
      </main>
      {!configLoading && !waitingListMode && <Footer />}
    </section>
  );
}
